import React from 'react'
import { faListUl } from '@fortawesome/pro-regular-svg-icons'
import { BoxV4, StatusChipV4 } from '@labrav/react-components'
import { INSTALLMENTS_COLLAPSIBLE_STYLE_V2 } from '../SubscriptionDetail.styles'
import { renderSubscriptionDetailHeadingIcon } from './renderSubscriptionDetailHeadingIcon'
import { SectionWrapper } from '../../../../common/components/SectionWrapper/SectionWrapper'
import { dateAndTimeConversion } from '../../../../common/utils/dateAndTimeConversion'
import { SubscriptionDetailObject } from '../../../../modules/subscription/reducer'

interface PaymentScheduleSectionProps {
  subscriptionDetail: SubscriptionDetailObject
  timeZone: string
  classes: any
  cloudType?: string
}

const getInstallmentStatusVariant = (
  status?: string | null
): 'success' | 'error' | 'warning' => {
  const s = (status || '').toUpperCase()
  if (s === 'DISBURSED') return 'success'
  if (s === 'DELAYED') return 'error'
  return 'warning'
}

export const PaymentScheduleSection: React.FC<PaymentScheduleSectionProps> = ({
  subscriptionDetail,
  timeZone,
  classes,
  cloudType,
}) => {
  if (cloudType === 'GCP' && subscriptionDetail) {
    const installments = subscriptionDetail.installments
    if (!installments || installments.length === 0) return null

    return (
      <div className={classes.boxWrapper}>
        <BoxV4
          variant="small"
          headingVariant="small"
          heading={`Installments(${installments.length})`}
          headingIcon={renderSubscriptionDetailHeadingIcon(classes)}
          collapsible
          collapsibleStyle={INSTALLMENTS_COLLAPSIBLE_STYLE_V2}
        >
          <div className={classes.installmentsContent}>
            <div className={classes.paymentScheduleTableV2}>
              <div className={classes.paymentScheduleHeaderV2}>
                <div className={classes.paymentHeaderCell}>Invoice date</div>
                <div className={classes.paymentHeaderCell}>
                  Installment amount
                </div>
                <div className={classes.paymentHeaderCell}>Free credit</div>
                <div className={classes.paymentHeaderCell}>Status</div>
              </div>
              {installments.map((item, index) => (
                <div key={index} className={classes.paymentScheduleRowV2}>
                  <div className={classes.paymentCell}>
                    {item.invoiceDate
                      ? dateAndTimeConversion(item.invoiceDate, timeZone)
                          .modifiedDate
                      : '-'}
                  </div>
                  <div className={classes.paymentCell}>
                    {item.installmentAmount != null ? (
                      <>
                        <span className={classes.currencySymbol}>$</span>
                        {item.installmentAmount.toLocaleString()}
                      </>
                    ) : (
                      '-'
                    )}
                  </div>
                  <div className={classes.paymentCell}>
                    {item.freeCredit != null ? (
                      <>
                        <span className={classes.currencySymbol}>$</span>
                        {item.freeCredit.toLocaleString()}
                      </>
                    ) : (
                      '-'
                    )}
                  </div>
                  <div className={classes.paymentCell}>
                    {item.status ? (
                      <StatusChipV4
                        label={item.status}
                        variant={getInstallmentStatusVariant(item.status)}
                        size="small"
                      />
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </BoxV4>
      </div>
    )
  }

  const hasRegularPaymentSchedule =
    subscriptionDetail?.paymentSchedule?.length > 0
  const hasAzurePaymentSchedule =
    (subscriptionDetail?.azurePaymentSchedule?.schedule?.length ?? 0) > 0

  if (!hasRegularPaymentSchedule && !hasAzurePaymentSchedule) {
    return null
  }

  const renderRegularPaymentSchedule = () => (
    <div className={classes.paymentScheduleTable}>
      <div className={classes.paymentScheduleHeader}>
        <div className={classes.paymentScheduleHeaderCell}>Invoice date</div>
        <div className={classes.paymentScheduleHeaderCell}>
          Installment amount
        </div>
      </div>
      {subscriptionDetail.paymentSchedule?.map(
        (
          payment: SubscriptionDetailObject['paymentSchedule'][number],
          index: number
        ) => (
          <div key={index} className={classes.paymentScheduleRow}>
            <div className={classes.paymentScheduleCell}>
              {payment.paymentDate
                ? dateAndTimeConversion(payment.paymentDate, timeZone)
                    .modifiedDate
                : '-'}
            </div>
            <div className={classes.paymentScheduleCell}>
              {payment.amount !== undefined && payment.amount !== null ? (
                <>
                  <span className={classes.currencySymbol}>
                    {cloudType === 'AWS'
                      ? payment.currencySymbol ||
                        subscriptionDetail.subscription?.currencySymbol ||
                        '$'
                      : '$'}
                  </span>
                  {payment.amount.toLocaleString()}
                </>
              ) : (
                '-'
              )}
            </div>
          </div>
        )
      )}
    </div>
  )

  const renderAzurePaymentSchedule = () => (
    <div>
      {/* Payment Schedule Table */}
      <div className={classes.paymentScheduleTable}>
        <div className={classes.paymentScheduleHeader}>
          <div className={classes.paymentScheduleHeaderCell}>Date</div>
          <div className={classes.paymentScheduleHeaderCell}>Amount</div>
        </div>
        {subscriptionDetail.azurePaymentSchedule?.schedule?.map(
          (payment, index) => (
            <div key={index} className={classes.paymentScheduleRow}>
              <div className={classes.paymentScheduleCell}>
                {payment.chargeDate
                  ? dateAndTimeConversion(payment.chargeDate, timeZone)
                      .modifiedDate
                  : dateAndTimeConversion(
                      subscriptionDetail.subscription.createdAt,
                      timeZone
                    ).modifiedDate}
              </div>
              <div className={classes.paymentScheduleCell}>
                {payment.pricePerPaymentInUsd !== undefined &&
                payment.pricePerPaymentInUsd !== null ? (
                  <>
                    <span className={classes.currencySymbol}>$</span>
                    {payment.pricePerPaymentInUsd.toLocaleString()}
                  </>
                ) : (
                  '-'
                )}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  )

  const getInstallmentCount = () => {
    if (hasAzurePaymentSchedule) {
      return subscriptionDetail.azurePaymentSchedule?.schedule?.length || 0
    }
    return subscriptionDetail.paymentSchedule?.length || 0
  }

  return (
    <SectionWrapper
      heading={`Installments(${getInstallmentCount()})`}
      icon={faListUl}
    >
      <div className={classes.sectionContent}>
        {hasAzurePaymentSchedule
          ? renderAzurePaymentSchedule()
          : renderRegularPaymentSchedule()}
      </div>
    </SectionWrapper>
  )
}
